import { createFileRoute, type ErrorComponentProps, Outlet } from '@tanstack/react-router';
import { Flex } from '@/components/Flex';
import { Page } from '@/components/Page';
import { RouteError } from '@/components/RouteError';
import { Skeleton } from '@/components/Skeleton';
import { SurveyPermissionProvider } from '@/permissions/SurveyPermissionProvider';
import { fetchSurveyPermissions } from '@/queries/surveys/client';

export const Route = createFileRoute('/_client/_auth/surveys')({
    component: SurveysLayout,
    loader: fetchSurveyPermissions,
    pendingComponent: SurveysLayoutSkeleton,
    errorComponent: SurveysLayoutError,
});

function SurveysLayoutError({ reset }: ErrorComponentProps) {
    return <RouteError kind="client" onRetry={reset} />;
}

// The tab-specific skeleton below this layout hasn't matched yet while
// `fetchSurveyPermissions` is pending, and this layout has no way to know which tab is
// being navigated to — so this only recreates the header row every tab shares
// (survey selector + PDF button), not any tab-specific content.
function SurveysLayoutSkeleton() {
    return (
        <Page.Root>
            <Page.Content gap="5">
                <Flex align="end" justify="space-between">
                    {/* Absolute widths: these sit in shrink-to-fit flex items, so a
                        percentage would have no definite width to resolve against. */}
                    <Flex direction="column" gap="2">
                        <Skeleton.Line width={96} height="sm" />
                        <Skeleton.Box width={300} height={42} />
                    </Flex>

                    <Skeleton.Box width={168} height={36} />
                </Flex>
            </Page.Content>
        </Page.Root>
    );
}

function SurveysLayout() {
    const { unrestricted, permissions } = Route.useLoaderData();

    return (
        <SurveyPermissionProvider {...(unrestricted ? { unrestricted: true } : { unrestricted: false, permissions })}>
            <Outlet />
        </SurveyPermissionProvider>
    );
}
